import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  clampCustomDay,
  deleteCustomer,
  isPaidOrSetUp,
  paymentMethodOf,
  setCustomerStatus,
  updateCustomer,
  watchCustomer,
  watchCustomerInvoices,
  watchSmsLog,
} from '../lib/data.js'
import { chargeCustomer, chargeInvoice, markInvoicePaidManual, sendInvoiceEmail } from '../lib/stripe.js'
import ConnectBankButton from '../components/ConnectBankButton.jsx'
import { fmtDate, fmtDateTime, fmtMoney, statusPillClass } from '../lib/format.js'
import SmsComposerModal from '../admin/SmsComposerModal.jsx'

export default function CustomerDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState(null)
  const [invoices, setInvoices] = useState({})
  const [editing, setEditing] = useState(false)
  const [flash, setFlash] = useState(null)
  const [showSms, setShowSms] = useState(false)
  const [smsLog, setSmsLog] = useState([])

  useEffect(() => watchCustomer(id, setCustomer), [id])
  useEffect(() => watchCustomerInvoices(id, setInvoices), [id])
  useEffect(() => watchSmsLog(setSmsLog, 200), [])
  const lastSms = useMemo(
    () => smsLog.find((r) => r.customerId === id && r.status === 'sent') || null,
    [smsLog, id],
  )

  const invoiceList = useMemo(() => Object.entries(invoices)
    .map(([k, v]) => ({ id: k, ...v }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [invoices])

  if (customer === null) return <p className="text-gray-500">Loading customer…</p>
  if (!customer) return (
    <div>
      <Link to="/customers" className="text-sm text-brand-green-deep hover:underline">← All customers</Link>
      <p className="text-gray-500 mt-3">Customer not found.</p>
    </div>
  )

  const onSuspend = async () => {
    if (customer.status === 'suspended') {
      await setCustomerStatus(id, 'active')
    } else {
      if (!confirm(`Suspend ${customer.name}? They'll be skipped on the next billing cycle.`)) return
      await setCustomerStatus(id, 'suspended')
    }
  }

  const onDelete = async () => {
    if (!confirm(`Delete ${customer.name}? This is permanent.`)) return
    await deleteCustomer(id)
    navigate('/customers')
  }

  const method = paymentMethodOf(customer)

  // Flip the customer onto the mail rail — paymentMethod:'mail',
  // autoDraft:false. No Stripe touch, no invoice changes. Future invoice
  // emails switch to the no-Pay-button "mail your check" variant.
  const onSetMail = async () => {
    setFlash(null)
    if (!confirm(`Set ${customer.name} as a MAIL-payment customer? Future invoice emails will show a "mail your check" panel with no Pay button.`)) return
    try {
      await updateCustomer(id, { paymentMethod: 'mail', autoDraft: false })
      setFlash({ kind: 'ok', msg: `${customer.name} is now on the mail-payment rail.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Could not update customer.' })
    }
  }

  // Admin marks an invoice paid manually (check, cash, etc). Flips
  // status to 'paid' on the server AND sends a slim "Thank you for your
  // payment" receipt via Resend. No Stripe charge.
  const onMarkPaid = async (inv) => {
    setFlash(null)
    if (!confirm(`Mark invoice #${inv.invoiceNumber} (${fmtMoney(inv.amount)}) as PAID and email a receipt to ${customer.email || customer.name}?`)) return
    try {
      const r = await markInvoicePaidManual(inv.id)
      if (!r?.ok) throw new Error(r?.error || 'Could not mark paid.')
      const msg = r.emailed
        ? `Invoice #${inv.invoiceNumber} marked paid — receipt sent to ${customer.email}`
        : `Invoice #${inv.invoiceNumber} marked paid — no email on file, receipt not sent`
      setFlash({ kind: 'ok', msg })
      // Auto-dismiss after 5 seconds.
      setTimeout(() => setFlash((f) => (f && f.msg === msg ? null : f)), 5000)
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Could not mark paid.' })
    }
  }

  // Charge the most recent OPEN invoice via Stripe.
  const onCharge = async () => {
    setFlash(null)
    if (!customer.stripePaymentMethodId) {
      setFlash({ kind: 'err', msg: 'No bank account on file. Click "Connect bank account" first.' })
      return
    }
    const open = invoiceList.find((inv) => inv.status === 'pending' || inv.status === 'sent')
    try {
      const res = open ? await chargeInvoice(open.id) : await chargeCustomer(id)
      setFlash({ kind: 'ok', msg: `Charge requested${res?.paymentIntentId ? ` (${res.paymentIntentId})` : ''}. ACH typically settles in 3–5 days.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Charge failed.' })
    }
  }

  const onSendInvoice = async () => {
    setFlash(null)
    try {
      const open = invoiceList.find((inv) => inv.status === 'pending' || inv.status === 'sent')
      if (!open) {
        setFlash({ kind: 'err', msg: 'No open invoice to send. Run a billing cycle first.' })
        return
      }
      await sendInvoiceEmail(open.id)
      setFlash({ kind: 'ok', msg: `Email queued for invoice #${open.invoiceNumber}.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Could not send.' })
    }
  }

  return (
    <div className="space-y-4">
      <Link to="/customers" className="text-sm text-brand-green-deep hover:underline">← All customers</Link>

      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">{customer.name}</h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-sm">
            <span className={statusPillClass(customer.status)}>{customer.status || 'active'}</span>
            <span className="text-gray-600">{customer.servicePlan || 'No plan'}</span>
            <span className="text-gray-400">·</span>
            <span className="font-bold">{fmtMoney(customer.monthlyAmount)} / {customer.billingCycle === '15th' ? '15th' : '1st'}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-secondary" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn-secondary" onClick={onSendInvoice}>Send invoice</button>
          <button
            className="btn-secondary disabled:opacity-50"
            onClick={() => setShowSms(true)}
            disabled={!customer.phone}
            title={customer.phone ? `Text ${customer.name}` : 'No phone on file'}>
            💬 Send SMS
          </button>
          <button className="btn-gold" onClick={onCharge}>Charge now</button>
          {paymentMethodOf(customer) !== 'mail' ? (
            <button
              className="btn-secondary"
              onClick={onSetMail}
              title="Flip this customer onto the mail-payment rail (no Stripe, mail-only invoice emails).">
              ✉️ Set as Mail Payment
            </button>
          ) : null}
          <button className="btn-secondary" onClick={onSuspend}>
            {customer.status === 'suspended' ? 'Reactivate' : 'Suspend'}
          </button>
          <button className="text-xs text-red-600 font-bold ml-2 self-center" onClick={onDelete}>Delete</button>
        </div>
      </div>

      {showSms ? (
        <SmsComposerModal customer={customer} customerId={id} onClose={() => setShowSms(false)} />
      ) : null}

      {flash ? (
        <div className={`rounded-lg border px-3 py-2 text-sm ${flash.kind === 'ok' ? 'bg-brand-mint border-brand-green text-brand-green-deep' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {flash.msg}
        </div>
      ) : null}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2 space-y-3">
          <h2 className="font-extrabold">Contact</h2>
          <Row label="Email" value={customer.email || '—'} />
          <Row label="Phone" value={customer.phone || '—'} />
          <Row label="Address" value={customer.address || '—'} />
          <Row label="City / State / Zip" value={formatCityStateZip(customer) || '—'} />
          <Row label="Last SMS" value={lastSms
            ? <span className="text-sm">
                <span className="font-bold">{fmtDateTime(lastSms.sentAt)}</span>
                <span className="text-gray-500"> · </span>
                <span className="text-gray-700 italic">"{(lastSms.message || '').slice(0, 90)}{(lastSms.message || '').length > 90 ? '…' : ''}"</span>
              </span>
            : '—'} />
          <Row label="Notes" value={customer.notes || '—'} />
        </div>
        <div className="card space-y-3">
          <h2 className="font-extrabold">Payment</h2>
          <Row label="Method" value={<PaymentMethodPill customer={customer} currentInvoice={invoiceList[0]} />} />
          {method === 'manual' ? (
            <>
              <Row label="Auto-draft" value="— Disabled (manual / check)" />
              <p className="text-[11px] text-gray-500">
                Customer receives an invoice email each month and pays by check or other
                means. No auto-pay rail is configured.
              </p>
            </>
          ) : method === 'stripe_invoice' ? (
            <>
              <Row label="Auto-draft" value="— Disabled (pay-on-click)" />
              <Row label="Stripe customer" value={customer.stripeCustomerId || '— Not linked'} mono />
              <Row label="Bank on file" value={customer.stripePaymentMethodId ? '✓ Verified' : '— None'} />
              <p className="text-[11px] text-gray-500">
                We email a monthly invoice with a Pay button. First time, the customer
                links their bank once via Stripe instant verification (no micro-deposits).
                After that it's one tap.
              </p>
            </>
          ) : method === 'card_auto' ? (
            <>
              <Row label="Auto-draft" value={customer.stripePaymentMethodId ? '✓ Enabled (card)' : '⌛ Awaiting card setup'} />
              <Row label="Stripe customer" value={customer.stripeCustomerId || '— Not linked'} mono />
              <Row
                label="Card on file"
                value={customer.cardBrand && customer.cardLast4
                  ? `✓ ${customer.cardBrand[0].toUpperCase()}${customer.cardBrand.slice(1)} ••${customer.cardLast4} (${customer.cardFunding || 'unknown'})`
                  : (customer.stripePaymentMethodId ? '✓ Saved' : '— None')}
              />
              <p className="text-[11px] text-gray-500">
                Card is saved once and charged automatically each month. The first invoice
                lets the customer enter their card; from then on the scheduler auto-charges
                on the due date. Credit cards include a ~3.3% processing fee; debit at face value.
              </p>
            </>
          ) : method === 'card_invoice' ? (
            <>
              <Row label="Auto-draft" value="— Disabled (pay-on-click)" />
              <Row label="Stripe customer" value={customer.stripeCustomerId || '— Not linked'} mono />
              <Row
                label="Card on file"
                value={customer.cardBrand && customer.cardLast4
                  ? `✓ ${customer.cardBrand[0].toUpperCase()}${customer.cardBrand.slice(1)} ••${customer.cardLast4} (${customer.cardFunding || 'unknown'})`
                  : (customer.stripePaymentMethodId ? '✓ Saved' : '— None')}
              />
              <p className="text-[11px] text-gray-500">
                We email a monthly invoice with a Pay button. The customer enters their
                card the first time and pays in one tap from then on. Credit-card
                processing fee passes through to the customer.
              </p>
            </>
          ) : (
            <>
              <Row label="Auto-draft" value={customer.autoDraft ? '✓ Enabled' : '— Disabled'} />
              <Row label="Stripe customer" value={customer.stripeCustomerId || '— Not linked'} mono />
              {customer.paymentMethodType === 'card' ? (
                <>
                  <Row
                    label="Card on file"
                    value={customer.cardBrand && customer.cardLast4
                      ? `✓ ${customer.cardBrand[0].toUpperCase()}${customer.cardBrand.slice(1)} ••${customer.cardLast4} (${customer.cardFunding || 'unknown'})`
                      : (customer.stripePaymentMethodId ? '✓ Verified' : '— None')}
                  />
                  <p className="text-[11px] text-gray-500">
                    Card auto-pay. Credit cards include a ~3.3% processing fee on each invoice; debit cards are charged at face value.
                  </p>
                </>
              ) : (
                <>
                  <Row label="Bank on file" value={customer.stripePaymentMethodId ? '✓ Verified' : '— None'} />
                  <ConnectBankButton customer={customer} customerId={id} />
                  <p className="text-[11px] text-gray-500">
                    ACH bank-account auto-draft via Stripe Financial Connections — no processing fee.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center">
          <h2 className="font-extrabold">Invoice history</h2>
          <Link to="/invoices" className="text-xs font-bold text-brand-green-deep hover:underline">All invoices →</Link>
        </div>
        {invoiceList.length === 0 ? (
          <p className="text-sm text-gray-500 mt-3">No invoices yet for this customer.</p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead className="text-xs uppercase tracking-widest text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Invoice #</th>
                <th className="text-left py-2">Period</th>
                <th className="text-left py-2">Due</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Paid</th>
                <th className="text-right py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {invoiceList.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="py-2 font-bold">#{inv.invoiceNumber}</td>
                  <td className="py-2">{inv.billingPeriod || '—'}</td>
                  <td className="py-2">{fmtDate(inv.dueDate)}</td>
                  <td className="py-2 text-right font-bold">{fmtMoney(inv.amount)}</td>
                  <td className="py-2"><span className={statusPillClass(inv.status)}>{inv.status}</span></td>
                  <td className="py-2 text-xs text-gray-600">{fmtDateTime(inv.paidAt)}</td>
                  <td className="py-2 text-right">
                    {inv.status !== 'paid' && inv.status !== 'voided' ? (
                      <button
                        type="button"
                        onClick={() => onMarkPaid(inv)}
                        className="text-xs font-bold text-brand-green-deep hover:underline">
                        Mark Paid
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <EditModal customer={customer} id={id} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  )
}

function formatCityStateZip(c) {
  const cityState = [c.city, c.state].filter(Boolean).join(', ')
  return [cityState, c.zip].filter(Boolean).join(' ')
}

// Payment-method pill — mirrors the rule used on the Customers list:
//   • Manual / Check customers: always "Manual / Check".
//   • Every other customer is Stripe. Until stripePaymentMethodId is
//     written to Firebase by the signup flow, we only know they're on
//     Stripe — show a neutral "Stripe" pill (no method promise).
//   • Once stripePaymentMethodId is present, render the confirmed
//     method: Auto-Draft, Bank on File, or Card on File.
// Realtime: parent uses watchCustomer (Firebase onValue), so a write to
// stripePaymentMethodId re-renders this badge automatically.
function PaymentMethodPill({ customer, currentInvoice }) {
  const m = paymentMethodOf(customer)
  if (m === 'mail') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 text-xs">
        <span>✉️</span><span>Mail</span>
      </span>
    )
  }
  if (!customer?.stripePaymentMethodId) {
    // Nothing on file (manual fallback OR unfinished setup) and unpaid → one
    // simple "No Payment Method" label. A one-time card payment saves no PM
    // but is still paid → "Paid (card)".
    if (!isPaidOrSetUp(customer, currentInvoice)) {
      return (
        <span className="inline-flex items-center gap-1 font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded px-2 py-0.5 text-xs">
          No Payment Method
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-green-800 bg-green-50 border border-green-200 rounded px-2 py-0.5 text-xs">
        <span>✓</span><span>Paid (card)</span>
      </span>
    )
  }
  if (m === 'stripe_invoice') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 text-xs">
        <span>✓</span><span>Bank on File</span>
      </span>
    )
  }
  if (m === 'card_auto' || m === 'card_invoice') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-pink-800 bg-pink-50 border border-pink-200 rounded px-2 py-0.5 text-xs">
        <span>✓</span><span>Card on File</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 font-extrabold text-brand-green-deep bg-brand-mint border border-brand-green rounded px-2 py-0.5 text-xs">
      <span>✓</span><span>Auto-Draft</span>
    </span>
  )
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`col-span-2 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}

function EditModal({ customer, id, onClose }) {
  // Seed paymentMethod from the canonical field if present, otherwise
  // infer from legacy autoDraft so older customer records get a sane
  // default on the radio.
  const initialMethod = paymentMethodOf(customer)
  const [form, setForm] = useState({ ...customer, paymentMethod: initialMethod })
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const submit = async (e) => {
    e.preventDefault()
    const method = ['stripe', 'stripe_invoice', 'card_auto', 'card_invoice', 'manual'].includes(form.paymentMethod)
      ? form.paymentMethod
      : 'stripe'
    const patch = {
      name: form.name || '',
      email: form.email || '',
      phone: form.phone || '',
      address: form.address || '',
      city: form.city || '',
      state: (form.state || '').toUpperCase(),
      zip: form.zip || '',
      servicePlan: form.servicePlan || '',
      monthlyAmount: Number(form.monthlyAmount) || 0,
      // Additive: '15th' and 'custom' pass through; anything else → '1st'.
      // Custom day fields are only stored for 'custom'; null removes them otherwise.
      billingCycle: ['15th', 'custom'].includes(form.billingCycle) ? form.billingCycle : '1st',
      customSendDay: form.billingCycle === 'custom' ? clampCustomDay(form.customSendDay) : null,
      customDueDay: form.billingCycle === 'custom' ? clampCustomDay(form.customDueDay) : null,
      paymentMethod: method,
      // autoDraft stays in sync with the auto-charge rails so legacy
      // scheduler + email-template code keeps working without churn.
      autoDraft: method === 'stripe' || method === 'card_auto',
      notes: form.notes || '',
    }
    await updateCustomer(id, patch)
    onClose()
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-30 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="font-extrabold">Edit customer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Name"><input className="input" value={form.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Email"><input type="email" className="input" value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field label="Phone"><input className="input" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          </div>
          <Field label="Address"><input className="input" value={form.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6"><Field label="City"><input className="input" value={form.city || ''} onChange={(e) => set('city', e.target.value)} /></Field></div>
            <div className="col-span-2"><Field label="State"><input className="input" maxLength={2} value={form.state || ''} onChange={(e) => set('state', e.target.value.toUpperCase())} /></Field></div>
            <div className="col-span-4"><Field label="Zip"><input className="input" inputMode="numeric" maxLength={10} value={form.zip || ''} onChange={(e) => set('zip', e.target.value)} /></Field></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Service plan"><input className="input" value={form.servicePlan || ''} onChange={(e) => set('servicePlan', e.target.value)} /></Field>
            <Field label="Monthly ($)"><input type="number" step="0.01" min="0" className="input" value={form.monthlyAmount ?? ''} onChange={(e) => set('monthlyAmount', e.target.value)} /></Field>
            <Field label="Cycle">
              <select className="input" value={form.billingCycle || '1st'} onChange={(e) => set('billingCycle', e.target.value)}>
                <option value="1st">1st</option>
                <option value="15th">15th</option>
                <option value="custom">Custom days</option>
              </select>
            </Field>
          </div>
          {form.billingCycle === 'custom' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Send day (5–27)">
                <input type="number" min="5" max="27" className="input" value={form.customSendDay ?? ''} onChange={(e) => set('customSendDay', e.target.value)} />
              </Field>
              <Field label="Due day (5–27)">
                <input type="number" min="5" max="27" className="input" value={form.customDueDay ?? ''} onChange={(e) => set('customDueDay', e.target.value)} />
              </Field>
            </div>
          )}
          <Field label="Payment method">
            <select
              className="input"
              value={form.paymentMethod || 'stripe'}
              onChange={(e) => set('paymentMethod', e.target.value)}
            >
              <option value="stripe">Stripe Auto-Draft</option>
              <option value="stripe_invoice">Stripe Invoice (Pay button)</option>
              <option value="card_auto">Card Auto-Pay</option>
              <option value="card_invoice">Card Invoice (Pay button)</option>
              <option value="manual">Manual / Check</option>
            </select>
          </Field>
          <Field label="Notes"><textarea className="input min-h-[80px]" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></Field>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}
